import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormComponentProps } from '../../global-forms/GlobalForms.types';
import globalStyles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  CreateMergeFieldPrefixDto,
  ICreateMergeFieldPrefixDto,
} from '@lmt/unicorn-shared/test/nock-client';
import { QueryFactory } from '@lmt/shared-types';
import { handleSubmitFormError } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';

import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { SubmitButton } from '../../common/ui-kit/buttons/SubmitButton';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { Field } from '../../common/Field';
import { Input } from '../../common/ui-kit/inputs/Input';
import { useEventCallback } from '@mui/material';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import i18next from 'i18next';

const codePrefixYupSchema: yup.Schema<Partial<ICreateMergeFieldPrefixDto>> =
  yup.object({
    prefix: yup
      .string()
      .trim()
      .max(3, () =>
        i18next.t('translation:errors.max_length', {
          maxLength: 3,
        }),
      )
      .matches(/^\S*$/, {
        excludeEmptyString: true,
        message: () => i18next.t('translation:errors.no_spaces'),
      })
      .required(() => i18next.t('translation:errors.required'))
      .matches(/^[aA-zZ\s]+$/, () =>
        i18next.t('translation:documents.query_builder.errors.only_letters'),
      ),
  });

const nameof = nameofFactory<CreateMergeFieldPrefixDto>();
export const CreatePrefixForm: React.FC<FormComponentProps> = (props) => {
  const { formType, formId, tryCompleteForm } = props;
  const i18n = useScopedTranslation('documents.query_builder');
  const modals = useModal();
  const queryClient = useQueryClient();

  const form = useAdvancedForm<CreateMergeFieldPrefixDto>(
    useEventCallback(async (data: CreateMergeFieldPrefixDto) => {
      try {
        await tryCompleteForm(
          formType,
          formId,
          async () => {
            await QueryFactory.MergeFieldPrefixClient.create({
              prefix: data.prefix,
            });

            await queryClient.invalidateQueries({
              queryKey: QueryFactory.MergeFieldPrefixQuery.getQueryKey(),
            });

            return data.prefix;
          },
          i18n.t('add_prefix_loading'),
        );
      } catch (err) {
        const errorDetails = handleSubmitFormError<CreateMergeFieldPrefixDto>(
          err,
          form.setError,
        );
        if (errorDetails.overallServerError) {
          await modals.showError({ text: errorDetails.overallServerError });
        }
      }
    }),
    undefined,
    {
      resolver: yupResolver(codePrefixYupSchema),
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );

  return (
    <form
      className={globalStyles.autoSizedContainer}
      onSubmit={form.handleSubmitDefault}
      data-test-id="createCodePrefix"
    >
      <div className={globalStyles.dataContainer}>
        <Field title={i18n.t('add_prefix_title')} testId="new-code-prefix">
          <Input
            {...form.register(nameof('prefix'))}
            errorText={form.formState.errors.prefix?.message}
            onKeyDown={(event) => {
              if (event.code === 'Space') event.preventDefault();
            }}
          />
        </Field>
      </div>
      <div className={globalStyles.submitContainer}>
        <SubmitButton
          submitting={form.formState.isSubmitting}
          className={globalStyles.submit}
          title={i18n.t('add_prefix_text')}
        />
      </div>
    </form>
  );
};
